import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import axiosConfig from './axiosConfig';
import { studentsDomain } from './domain';
import {
    buildFirebaseConfigFromEnv,
    buildFirebaseConfigFromServer,
    isFirebaseConfigReady
} from './firebaseConfig';

export const PUSH_PROMPT_STATUS_KEY = 'pushNotificationPromptStatus';
/** Device FCM token last registered with the server for the current browser session. */
export const WEB_FCM_DEVICE_TOKEN_KEY = 'web_fcm_device_token';

let messagingInstance = null;
let cachedConfig = null;
let focusRefreshAttached = false;

/**
 * Clear cached Firebase config (e.g. after logout).
 */
export function clearFirebaseConfigCache() {
    cachedConfig = null;
    messagingInstance = null;
}

/**
 * @returns {string}
 */
function getApiOrigin() {
    const base = studentsDomain || '';
    return base.replace(/\/$/, '');
}

/**
 * Load push config from server (no auth) — production source of truth.
 * @returns {Promise<{ firebaseOptions: object, vapidKey: string, configured?: boolean } | null>}
 */
export async function fetchPublicPushConfig() {
    try {
        const origin = getApiOrigin();
        const response = await fetch(`${origin}/api/students/push-config-public`, {
            credentials: 'same-origin'
        });
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        const merged = buildFirebaseConfigFromServer(data);
        return isFirebaseConfigReady(merged) ? merged : null;
    } catch (err) {
        console.warn('Failed to load public push config', err);
        return null;
    }
}

/**
 * @param {{ preferServer?: boolean, forceRefresh?: boolean }} [options]
 * @returns {Promise<{ firebaseOptions: object, vapidKey: string } | null>}
 */
export async function resolveFirebaseConfig(options = {}) {
    const { preferServer = true, forceRefresh = false } = options;

    if (cachedConfig && !forceRefresh) {
        return cachedConfig;
    }

    if (preferServer) {
        try {
            const response = await axiosConfig.get('/students/push-config');
            const merged = buildFirebaseConfigFromServer(response.data);
            if (isFirebaseConfigReady(merged)) {
                cachedConfig = merged;
                return cachedConfig;
            }
        } catch (err) {
            console.warn('Failed to load push config from server (auth)', err);
        }

        const fromPublic = await fetchPublicPushConfig();
        if (fromPublic) {
            cachedConfig = fromPublic;
            return cachedConfig;
        }
    }

    const fromEnv = buildFirebaseConfigFromEnv();
    if (isFirebaseConfigReady(fromEnv)) {
        cachedConfig = fromEnv;
        return cachedConfig;
    }

    if (!preferServer) {
        const fromPublic = await fetchPublicPushConfig();
        if (fromPublic) {
            cachedConfig = fromPublic;
            return cachedConfig;
        }
    }

    return null;
}

/**
 * @returns {boolean}
 */
export function isPushNotificationSupported() {
    return (
        typeof window !== 'undefined' &&
        'Notification' in window &&
        'serviceWorker' in navigator
    );
}

/**
 * @returns {Promise<boolean>}
 */
export async function isFirebaseMessagingSupported() {
    try {
        return await isSupported();
    } catch {
        return false;
    }
}

/**
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function registerFirebaseServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/'
        });
        await navigator.serviceWorker.ready;

        const config = await resolveFirebaseConfig({ preferServer: true });
        if (config?.firebaseOptions?.appId && registration.active) {
            registration.active.postMessage({
                type: 'firebase-push-config',
                appId: config.firebaseOptions.appId
            });
        }

        return registration;
    } catch (err) {
        console.error('Service worker registration failed:', err);
        return null;
    }
}

/**
 * Ensure Firebase messaging + service worker are ready.
 * @returns {Promise<{ config: object|null, messaging: import('firebase/messaging').Messaging|null, registration: ServiceWorkerRegistration|null, reason?: string }>}
 */
export async function ensureMessagingReady() {
    const config = await resolveFirebaseConfig({ preferServer: true });
    if (!config) {
        return { config: null, messaging: null, registration: null, reason: 'not_configured' };
    }

    const supported = await isFirebaseMessagingSupported();
    if (!supported) {
        return { config, messaging: null, registration: null, reason: 'not_supported' };
    }

    const registration = await registerFirebaseServiceWorker();
    if (!registration) {
        return { config, messaging: null, registration: null, reason: 'service_worker_failed' };
    }

    try {
        if (!messagingInstance) {
            const apps = getApps();
            const app = apps.length > 0 ? apps[0] : initializeApp(config.firebaseOptions);
            messagingInstance = getMessaging(app);
        }
        return { config, messaging: messagingInstance, registration };
    } catch (err) {
        console.error('Firebase messaging init failed:', err);
        return { config, messaging: null, registration, reason: 'messaging_init_failed' };
    }
}

/**
 * Re-sync FCM token when the tab becomes visible (parity with Android onNewToken / resume).
 */
export function attachPushTokenRefreshOnFocus() {
    if (focusRefreshAttached || typeof document === 'undefined') {
        return;
    }
    focusRefreshAttached = true;

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            syncPushTokenIfGranted({ preferServer: true }).catch(() => {});
        }
    });
}

/**
 * Show a native browser notification (when permission is granted).
 * @param {string} title
 * @param {string} body
 */
export function showBrowserNotification(title, body) {
    if (!isPushNotificationSupported() || Notification.permission !== 'granted') {
        return;
    }

    try {
        const notification = new Notification(title, {
            body,
            icon: `${process.env.PUBLIC_URL || ''}/favicon.ico`,
            tag: `vector-${Date.now()}`
        });
        notification.onclick = () => {
            window.focus();
            notification.close();
        };
    } catch (err) {
        console.warn('Could not show browser notification:', err);
    }
}

/**
 * @param {string} token
 */
export async function registerFcmTokenWithServer(token) {
    await axiosConfig.post('/students/fcm-token', {
        token,
        platform: 'web'
    });
    localStorage.setItem(WEB_FCM_DEVICE_TOKEN_KEY, token);
}

/**
 * Remove this device's FCM token for the currently logged-in student.
 * @param {string} token
 */
export async function unregisterFcmTokenWithServer(token) {
    if (!token) {
        return;
    }

    await axiosConfig.delete('/students/fcm-token', {
        data: {
            token,
            platform: 'web'
        }
    });
}

/**
 * Unregister the stored device token before logout or account switch.
 */
export async function unregisterPushTokenBeforeLogout() {
    const token = localStorage.getItem(WEB_FCM_DEVICE_TOKEN_KEY);
    if (!token) {
        clearFirebaseConfigCache();
        return;
    }

    try {
        await unregisterFcmTokenWithServer(token);
    } catch (err) {
        console.warn('FCM token unregister failed:', err);
    } finally {
        localStorage.removeItem(WEB_FCM_DEVICE_TOKEN_KEY);
        clearFirebaseConfigCache();
    }
}

/**
 * Obtain FCM token and register with server (same flow as Android syncFcmToken).
 * @param {{ requestPermission?: boolean }} [options]
 * @returns {Promise<{ success: boolean, reason?: string, error?: string }>}
 */
export async function registerWebPushDevice(options = {}) {
    const { requestPermission = false } = options;

    if (!isPushNotificationSupported()) {
        return { success: false, reason: 'unsupported' };
    }

    let permission = Notification.permission;
    if (permission === 'default' && requestPermission) {
        permission = await Notification.requestPermission();
    }

    if (permission !== 'granted') {
        return {
            success: false,
            reason: permission === 'denied' ? 'denied' : 'dismissed'
        };
    }

    const { config, messaging, registration, reason } = await ensureMessagingReady();
    if (!config || !messaging || !registration) {
        return { success: false, reason: reason || 'messaging_unavailable' };
    }

    try {
        const token = await getToken(messaging, {
            vapidKey: config.vapidKey,
            serviceWorkerRegistration: registration
        });

        if (!token) {
            return { success: false, reason: 'no_token' };
        }

        await registerFcmTokenWithServer(token);
        localStorage.setItem(PUSH_PROMPT_STATUS_KEY, 'enabled');
        return { success: true };
    } catch (err) {
        console.error('FCM getToken failed:', err);
        return { success: false, reason: 'token_error', error: err?.message };
    }
}

/**
 * Request browser permission and register FCM token with the server.
 * @returns {Promise<{ success: boolean, reason?: string, error?: string }>}
 */
export async function enablePushNotifications() {
    return registerWebPushDevice({ requestPermission: true });
}

/**
 * Sync token when permission was granted earlier (no prompt).
 * @param {{ preferServer?: boolean, forceRefresh?: boolean }} [options]
 * @returns {Promise<{ synced: boolean, reason?: string, error?: string }>}
 */
export async function syncPushTokenIfGranted(options = {}) {
    if (!isPushNotificationSupported() || Notification.permission !== 'granted') {
        return { synced: false, reason: 'permission_not_granted' };
    }

    if (options.forceRefresh) {
        clearFirebaseConfigCache();
    }

    const result = await registerWebPushDevice({ requestPermission: false });
    if (result.success) {
        return { synced: true };
    }
    return {
        synced: false,
        reason: result.reason,
        error: result.error
    };
}

/**
 * @param {(payload: import('firebase/messaging').MessagePayload) => void} callback
 * @returns {Promise<(() => void) | null>}
 */
export async function subscribeToForegroundMessages(callback) {
    const { messaging, reason } = await ensureMessagingReady();
    if (!messaging) {
        console.warn('Foreground FCM listener not started:', reason);
        return null;
    }

    return onMessage(messaging, callback);
}

/**
 * @param {import('firebase/messaging').MessagePayload} payload
 * @returns {{ title: string, body: string }}
 */
export function getNotificationDisplayFromPayload(payload) {
    const title =
        payload.notification?.title ||
        payload.data?.title ||
        'Vector India';
    const body =
        payload.notification?.body ||
        payload.data?.body ||
        '';
    return { title, body };
}

/**
 * Handle an incoming FCM payload in the active tab.
 * @param {import('firebase/messaging').MessagePayload} payload
 */
export function handleIncomingPushPayload(payload) {
    const { title, body } = getNotificationDisplayFromPayload(payload);
    showBrowserNotification(title, body);
    return { title, body };
}
